import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface NavigationContextType {
  navigateToNewResearch: (clientId?: string) => void;
  navigateToResearch: (researchId: string) => void;
  navigateToContractReview: (documentId: string) => void;
  navigateBackFromResearch: () => void;
  navigateBackFromNewResearch: () => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

interface NavigationProviderProps {
  children: React.ReactNode;
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const navigateToNewResearch = useCallback(
    (clientId?: string) => {
      if (clientId) {
        navigate('/new-research', { state: { clientId, returnTo: location.pathname } });
      } else {
        navigate('/new-research');
      }
    },
    [navigate, location.pathname]
  );

  const navigateToResearch = useCallback(
    (researchId: string) => {
      navigate(`/research/${researchId}`, {
        state: { returnTo: location.pathname },
      });
    },
    [navigate, location.pathname]
  );

  const navigateToContractReview = useCallback(
    (documentId: string) => {
      navigate(`/vault-document/${documentId}`, {
        state: { returnTo: location.pathname },
      });
    },
    [navigate, location.pathname]
  );

  const navigateBackFromResearch = useCallback(() => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate('/dashboard');
    }
  }, [navigate]);

  const navigateBackFromNewResearch = useCallback(() => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate('/dashboard');
    }
  }, [navigate]);

  const value: NavigationContextType = useMemo(
    () => ({
      navigateToNewResearch,
      navigateToResearch,
      navigateToContractReview,
      navigateBackFromResearch,
      navigateBackFromNewResearch,
    }),
    [
      navigateToNewResearch,
      navigateToResearch,
      navigateToContractReview,
      navigateBackFromResearch,
      navigateBackFromNewResearch,
    ]
  );

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
};
